<template>
  <div class="inventory-items-container inventory-items-page--glass glass-tab-page">
    <BrucdcumHeader
      :breadcrumb="['Inventory', 'Inventory Items']"
      :show-back-button="true"
      :back-fallback="backFallbackRoute"
    />

    <div class="glass-tab-view glass-tab-view--flush ii-glass-shell">
      <div class="glass-tab-view__content ii-page-stack">
        <div class="ii-header ii-glass-panel glass-tab-panel--nested">
          <div class="ii-header-title">
            <div class="ii-title-row">
              <h2 class="ii-title-heading">
                <span v-if="companyName" class="ii-title-company">{{ companyName }}</span>
                <span v-if="companyName" class="ii-title-sep" aria-hidden="true">·</span>
                <span>Inventory Items</span>
              </h2>
              <div class="ii-title-actions">
                <SecondaryButton
                  label="Search"
                  icon="bi bi-search"
                  custom-class="ii-action-btn"
                  @click="onSearchClick"
                />
                <PrimaryButton
                  label="Add a new inventory item"
                  icon="bi bi-plus-lg"
                  @click="goToAddNewItem"
                />
              </div>
            </div>
            <div class="ii-report-info">
              <span>Reorder Point: {{ reorderPointFilterLabel }}</span>
              <span>Current View: {{ currentViewFilterLabel }}</span>
              <span>{{ filteredItems.length }} item{{ filteredItems.length === 1 ? '' : 's' }}</span>
            </div>
          </div>

          <div class="ii-header-filters">
            <div class="ii-filter-field ii-filter-field--reorder">
              <LabelDropdown
                label="Reorder Point"
                select-id="ii-reorder-point"
                v-model="reorderPointFilter"
                inline
                flex
                :searchable="false"
                menu-class="ii-dropdown-menu"
                :options="reorderPointFilterOptions"
              />
            </div>
            <div class="ii-filter-field ii-filter-field--view">
              <LabelDropdown
                label="Current View"
                select-id="ii-current-view"
                v-model="currentViewFilter"
                inline
                flex
                :searchable="false"
                menu-class="ii-dropdown-menu"
                :options="currentViewFilterOptions"
              />
            </div>
          </div>
        </div>

        <div class="reports-account-glass-panel ii-table-panel glass-tab-panel--nested">
          <div v-if="loading" class="ii-loading-message">Loading inventory items...</div>
          <div v-if="errorMessage" class="ii-error-message">{{ errorMessage }}</div>

          <div v-if="!loading && !filteredItems.length" class="consultant-glass-table-no-records">
            No records to show
          </div>
          <div v-else-if="!loading" class="consultant-glass-table-section reports-account-glass-section">
            <div class="reports-account-glass-header">
              <div class="reports-account-glass-header-title">
                <i class="bi bi-box-seam reports-account-glass-header-icon" aria-hidden="true"></i>
                Inventory Items
              </div>
            </div>
            <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table reports-account-glass-table ii-glass-table">
                  <thead>
                    <tr>
                      <th class="ii-col-active">Active</th>
                      <th class="ii-col-name">Item Name</th>
                      <th class="ii-col-desc">Description</th>
                      <th class="ii-col-type">Type</th>
                      <th class="ii-col-qty">On Hand Qty</th>
                      <th class="ii-col-reorder">Reorder Point</th>
                      <th class="ii-col-price">Sales Price</th>
                      <th class="ii-col-price">Purchase Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, idx) in filteredItems"
                      :key="item.id"
                      class="ii-item-row consultant-glass-table-row"
                      :class="'ii-status-' + item.status"
                      :style="{ '--ii-row-delay': `${Math.min(idx * 0.03, 0.45)}s` }"
                    >
                      <td class="ii-col-active">
                        <input
                          type="checkbox"
                          :checked="item.status === 'active'"
                          disabled
                          class="ii-status-checkbox"
                          :title="item.status === 'active' ? 'Active' : 'Inactive'"
                        />
                      </td>
                      <td class="ii-col-name">
                        <span class="ii-cell-ellipsis" :title="item.name">{{ item.name }}</span>
                      </td>
                      <td class="ii-col-desc">
                        <span class="ii-cell-ellipsis" :title="item.description">{{ item.description }}</span>
                      </td>
                      <td class="ii-col-type">{{ item.type }}</td>
                      <td class="ii-col-qty">{{ item.qty }}</td>
                      <td class="ii-col-reorder">
                        <span :class="item.reorderPoint === 'Reached' ? 'ii-reorder-reached' : 'ii-reorder-safe'">
                          {{ item.reorderPoint }}
                        </span>
                      </td>
                      <td class="ii-col-price">{{ item.salesPrice }}</td>
                      <td class="ii-col-price">{{ item.purchasePrice }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '../styles/InventoryItems.css';
import '../styles/inventory-items-glass.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

const REORDER_POINT_FILTER_OPTIONS = Object.freeze([
  { value: 'all', label: 'All' },
  { value: 'safe', label: 'Safe' },
  { value: 'reached', label: 'Reached' },
]);

const CURRENT_VIEW_FILTER_OPTIONS = Object.freeze([
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]);

export default {
  name: 'InventoryItems',
  components: {
    BrucdcumHeader,
    LabelDropdown,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      reorderPointFilter: 'all',
      currentViewFilter: 'active',
      items: [],
      errorMessage: '',
      loading: false,
      reorderPointFilterOptions: REORDER_POINT_FILTER_OPTIONS,
      currentViewFilterOptions: CURRENT_VIEW_FILTER_OPTIONS,
    };
  },
  computed: {
    backFallbackRoute() {
      const companyId = this.$route.params.id;
      return companyId ? `/company/${companyId}/dashboard/inventory/center` : '';
    },
    companyName() {
      const storeCompany = this.$store.state.companies?.currentCompany;
      if (storeCompany?.name) return storeCompany.name;
      return this.$store.state.currentCompany?.name || '';
    },
    reorderPointFilterLabel() {
      const option = this.reorderPointFilterOptions.find((o) => o.value === this.reorderPointFilter);
      return option?.label || 'All';
    },
    currentViewFilterLabel() {
      const option = this.currentViewFilterOptions.find((o) => o.value === this.currentViewFilter);
      return option?.label || 'Active';
    },
    filteredItems() {
      return this.items.filter((item) => {
        const reorderMatch =
          this.reorderPointFilter === 'all' ||
          item.reorderPoint.toLowerCase() === this.reorderPointFilter;
        const statusMatch = item.status === this.currentViewFilter;
        return reorderMatch && statusMatch;
      });
    },
  },
  mounted() {
    this.fetchItems();
    this.setupInventoryRealtime();
  },
  beforeUnmount() {
    this.teardownInventoryRealtime();
  },
  methods: {
    setupInventoryRealtime() {
      if (this.inventoryOnHandListener) return;
      this.inventoryOnHandListener = (event) => this.handleInventoryOnHandUpdated(event);
      window.addEventListener('inventory-on-hand-updated', this.inventoryOnHandListener);
      import('@/services/companyPresenceClient')
        .then((m) => m.connectCompanyPresence())
        .catch(() => {});
    },
    teardownInventoryRealtime() {
      if (typeof this.inventoryOnHandListener === 'function') {
        window.removeEventListener('inventory-on-hand-updated', this.inventoryOnHandListener);
        this.inventoryOnHandListener = null;
      }
    },
    handleInventoryOnHandUpdated(event) {
      const detail = event?.detail || {};
      const eventCompanyId = Number(detail.companyId);
      const routeCompanyId = Number(this.$route.params.id);
      if (
        Number.isFinite(eventCompanyId) &&
        Number.isFinite(routeCompanyId) &&
        eventCompanyId !== routeCompanyId
      ) {
        return;
      }

      const updates = Array.isArray(detail.items) ? detail.items : [];
      if (!updates.length) {
        this.fetchItems();
        return;
      }

      const byId = new Map(
        updates
          .map((row) => [Number(row.id), row])
          .filter(([id]) => Number.isFinite(id))
      );
      if (!byId.size) return;

      let changed = false;
      this.items = this.items.map((item) => {
        const update = byId.get(Number(item.id));
        if (!update) return item;
        changed = true;
        const onHand = Number(update.onHandQuantity);
        const reorderPointValue =
          update.reorderPoint != null ? Number(update.reorderPoint) : item.reorderPointValue;
        return {
          ...item,
          onHandQuantity: onHand,
          qty: onHand,
          reorderPointValue,
          reorderPoint:
            reorderPointValue != null &&
            Number.isFinite(reorderPointValue) &&
            onHand <= reorderPointValue
              ? 'Reached'
              : 'Safe',
        };
      });

      // If socket reported items not currently in the list, refresh fully
      if (!changed || updates.some((row) => !this.items.some((i) => Number(i.id) === Number(row.id)))) {
        this.fetchItems();
      }
    },
    async fetchItems() {
      const companyId = this.$route.params.id;
      const url = `${process.env.VUE_APP_API_URL}/inventory/companies/${companyId}/items`;
      this.errorMessage = '';
      this.loading = true;
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch inventory items');
        const data = await response.json();
        this.items = data.map((item) => ({
          ...item,
          status: item.active ? 'active' : 'inactive',
          description: item.descriptionSales || item.descriptionPurchase || '',
          type: 'Inventory',
          qty: item.onHandQuantity,
          reorderPointValue: item.reorderPoint != null ? Number(item.reorderPoint) : null,
          reorderPoint:
            item.reorderPoint && item.onHandQuantity <= item.reorderPoint ? 'Reached' : 'Safe',
          salesPrice: item.salesPrice ? `$${item.salesPrice}` : '',
          purchasePrice: item.purchasePrice ? `$${item.purchasePrice}` : '',
        }));
      } catch (error) {
        this.errorMessage = error.message || 'Failed to fetch inventory items';
      } finally {
        this.loading = false;
      }
    },
    goToAddNewItem() {
      this.$router.push(`/company/${this.$route.params.id}/dashboard/inventory/new-item`);
    },
    onSearchClick() {
      alert('Search functionality coming soon!');
    },
  },
};
</script>
